// ============================================================
// BLOQUE 32 — RFW-READDY-NOTIFICATIONS-001
// Servicio de notificaciones.
// Solo-lectura + marcar leídas desde frontend.
// El backend PHP es responsable de insertar, entregar y auditar.
// ============================================================

import { getSupabaseClient } from '@/services/supabase/client';
import type {
  Notification,
  NotificationFilters,
  NotificationPage,
  UnreadCount,
  OnNewNotification,
  RealtimeSubscriptionStatus,
} from '@/types/notifications';

// ─── Helpers ────────────────────────────────────────────────

function getProfileId(): string {
  // En producción esto vendría de getSession().user.id.
  // Para el scope actual del bloque, el cliente Supabase ya maneja la auth.
  return 'authenticated';
}

function buildQuery(
  query: ReturnType<ReturnType<typeof getSupabaseClient>['from']>['select'],
  filters: NotificationFilters,
) {
  if (filters.type) {
    query = query.eq('type', filters.type);
  }
  if (filters.is_read !== undefined) {
    query = query.eq('is_read', filters.is_read);
  }
  if (filters.priority) {
    query = query.eq('priority', filters.priority);
  }
  if (filters.related_entity_type) {
    query = query.eq('related_entity_type', filters.related_entity_type);
  }
  if (filters.related_entity_id) {
    query = query.eq('related_entity_id', filters.related_entity_id);
  }
  if (filters.search) {
    query = query.or(
      `title.ilike.%${filters.search}%,message.ilike.%${filters.search}%`,
    );
  }
  return query;
}

function buildOrder(sort?: string) {
  switch (sort) {
    case 'oldest':
      return { column: 'created_at', ascending: true };
    case 'priority':
      // Prioridad urgente > alta > normal, luego por fecha
      return { column: 'priority_order', ascending: false };
    case 'newest':
    default:
      return { column: 'created_at', ascending: false };
  }
}

function buildPagination(page: number, perPage: number) {
  const from = (page - 1) * perPage;
  const to = from + perPage - 1;
  return { from, to };
}

// ─── Lectura ────────────────────────────────────────────────

/**
 * Lista las notificaciones del usuario autenticado con paginación y filtros.
 * RLS garantiza que solo ve sus propias notificaciones.
 */
export async function getMyNotifications(
  filters: NotificationFilters = {},
): Promise<NotificationPage> {
  const supabase = getSupabaseClient();
  const page = filters.page || 1;
  const perPage = filters.per_page || 20;
  const { from, to } = buildPagination(page, perPage);
  const order = buildOrder(filters.sort);

  let query = supabase
    .from('notifications')
    .select('*', { count: 'exact' });

  query = buildQuery(query, filters);
  query = query.order(order.column, { ascending: order.ascending }).range(from, to);

  const { data, error, count } = await query;

  if (error) throw new Error(`Error al obtener notificaciones: ${error.message}`);

  // Obtener conteo de no leídas en paralelo (el count anterior ya incluye filtros)
  const { count: unreadCount } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('is_read', false);

  return {
    notifications: (data || []) as Notification[],
    total: count || 0,
    page,
    per_page: perPage,
    unread_count: unreadCount || 0,
  };
}

/**
 * Obtiene solo las notificaciones no leídas del usuario.
 */
export async function getUnreadNotifications(): Promise<Notification[]> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('is_read', false)
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) throw new Error(`Error al obtener no leídas: ${error.message}`);

  return (data || []) as Notification[];
}

/**
 * Obtiene el conteo de notificaciones no leídas, desglosado por prioridad.
 */
export async function getUnreadCount(): Promise<UnreadCount> {
  const supabase = getSupabaseClient();

  const { count: total } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('is_read', false);

  const { count: urgent } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('is_read', false)
    .eq('priority', 'urgent');

  const { count: high } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('is_read', false)
    .eq('priority', 'high');

  return {
    total: total || 0,
    urgent: urgent || 0,
    high: high || 0,
  };
}

/**
 * Obtiene una notificación individual por ID.
 * RLS garantiza que solo pueda acceder a las propias.
 */
export async function getNotificationById(
  notificationId: string,
): Promise<Notification | null> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('id', notificationId)
    .maybeSingle();

  if (error) throw new Error(`Error al obtener notificación: ${error.message}`);

  return data as Notification | null;
}

// ─── Escritura limitada ────────────────────────────────────

/**
 * Marca una notificación como leída.
 * Solo el dueño puede marcarla (RLS).
 */
export async function markAsRead(notificationId: string): Promise<void> {
  const supabase = getSupabaseClient();

  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId);

  if (error) throw new Error(`Error al marcar como leída: ${error.message}`);
}

/**
 * Marca todas las notificaciones del usuario como leídas.
 * Solo afecta las propias (RLS).
 */
export async function markAllAsRead(): Promise<number> {
  const supabase = getSupabaseClient();

  const { error, count } = await supabase
    .from('notifications')
    .update({ is_read: true }, { count: 'exact' })
    .eq('is_read', false);

  if (error) throw new Error(`Error al marcar todas como leídas: ${error.message}`);

  return count || 0;
}

// ============================================================
// REALTIME
// Solo nuevas notificaciones del usuario autenticado.
// Sin suscripción a notificaciones de otros usuarios.
// ============================================================

let realtimeChannel: ReturnType<ReturnType<typeof getSupabaseClient>['channel']> | null = null;
let subscriptionStatus: RealtimeSubscriptionStatus = 'unsubscribed';

/**
 * Se suscribe a nuevas notificaciones del usuario autenticado vía Supabase Realtime.
 * IMPORTANTE: Llamar `unsubscribeFromNotifications()` al desmontar el componente.
 */
export function subscribeToNotifications(
  onNewNotification: OnNewNotification,
): () => void {
  const supabase = getSupabaseClient();

  // Cerrar canal previo si existe para evitar duplicados
  if (realtimeChannel) {
    supabase.removeChannel(realtimeChannel);
    realtimeChannel = null;
  }

  const channel = supabase
    .channel('notifications-own')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        // RLS garantiza que solo recibimos inserts cuyo profile_id coincida con auth.uid()
      },
      (payload) => {
        const notification = payload.new as Notification;
        onNewNotification(notification);
      },
    )
    .subscribe((status) => {
      switch (status) {
        case 'SUBSCRIBED':
          subscriptionStatus = 'subscribed';
          break;
        case 'CHANNEL_ERROR':
        case 'TIMED_OUT':
          subscriptionStatus = 'error';
          break;
        case 'CLOSED':
          subscriptionStatus = 'unsubscribed';
          break;
        default:
          break;
      }
    });

  realtimeChannel = channel;
  subscriptionStatus = 'subscribed';

  // Retornar función de cleanup
  return () => {
    unsubscribeFromNotifications();
  };
}

/**
 * Cierra la suscripción Realtime y limpia el listener.
 * Debe llamarse al desmontar el componente.
 */
export function unsubscribeFromNotifications(): void {
  if (realtimeChannel) {
    const supabase = getSupabaseClient();
    supabase.removeChannel(realtimeChannel);
    realtimeChannel = null;
  }
  subscriptionStatus = 'unsubscribed';
}

/**
 * Retorna el estado actual de la suscripción Realtime.
 */
export function getRealtimeSubscriptionStatus(): RealtimeSubscriptionStatus {
  return subscriptionStatus;
}

// ============================================================
// CONTRATO — LO QUE EL FRONTEND NUNCA DEBE HACER
// ============================================================
//
// ❌ insertar notificaciones
// ❌ actualizar delivery_status
// ❌ marcar notificación como enviada (sent/delivered)
// ❌ enviar email
// ❌ enviar SMS
// ❌ llamar servicios push
// ❌ usar credenciales de proveedor externo
// ❌ suscribirse a notificaciones de otros usuarios
//
// El backend PHP es responsable de:
// - plantillas, jobs, reintentos, proveedores, entrega, auditoría